/**
 * Ticket Lifecycle v2 — Slice 4E (+ slice 5A.3 widening)
 *
 * Pure computation of the first reliable Service Advisor Passport
 * metrics from existing event sources:
 *
 *   Counts (from advisor_action_events, slice 4B):
 *     - ticketsAcceptedCount             (action_kind === 'ticket_accepted')
 *     - ticketsKickedBackToTechCount     (action_kind === 'kicked_back_to_tech')
 *
 *   Timing (from ticket_custody_events, slice 2 — paired transitions):
 *     - averageAcceptLagMinutes          (kick_to_foh → foh_accept)
 *     - p50AcceptLagMinutes
 *     - p95AcceptLagMinutes
 *     - averageFohCustodyMinutes         (foh_accept  → kick_to_boh)
 *     - averageAdvisorLifecycleMinutes   (kick_to_foh → boh_accept,
 *                                         anchored on this advisor's
 *                                         foh_accept; slice 5A.3)
 *
 *   Current snapshot (from ticket_custody current-state):
 *     - currentPendingAdvisorAcceptCount  (shop-wide; no advisor owns yet)
 *     - currentWithAdvisorCount           (shop-wide; could be widened to
 *                                          per-advisor in a future slice)
 *
 * Pure: no I/O, no clock reads. Caller supplies the event slices and
 * current counts; the helper reduces them deterministically.
 *
 * "Insufficient data" rule:
 *   - Counts always return a real number (0 is a valid count).
 *   - Mean/p50/p95 return `null` when the underlying sample is empty.
 *   - Percentiles additionally return `null` when sample < 3 (too few
 *     points for the percentile to be meaningful). Mean is reported
 *     down to a single sample.
 */

import type {
  AdvisorActionKind,
  CustodyEventKind,
} from '@/lib/tickets/lifecycle/types';

export interface AdvisorActionEventLite {
  action_kind: AdvisorActionKind;
  created_at: string;
  ticket_id: string;
}

export interface CustodyEventLite {
  event_kind: CustodyEventKind;
  ticket_id: string;
  actor_id: string | null;
  created_at: string;
}

export interface AdvisorMetricsInput {
  advisorActionEvents: ReadonlyArray<AdvisorActionEventLite>;
  custodyEvents: ReadonlyArray<CustodyEventLite>;
  advisorUserId: string;
  currentCounts: {
    pendingAdvisorAccept: number;
    withAdvisor: number;
  };
}

export interface AdvisorMetrics {
  ticketsAcceptedCount: number;
  ticketsKickedBackToTechCount: number;
  averageAcceptLagMinutes: number | null;
  p50AcceptLagMinutes: number | null;
  p95AcceptLagMinutes: number | null;
  averageFohCustodyMinutes: number | null;
  /**
   * Slice 5A.3 — mean total advisor handoff cycle time:
   * `boh_accept.created_at − kick_to_foh.created_at`, recorded only for
   * cycles where THIS advisor's `foh_accept` was the accept event.
   * `null` when no complete cycle exists in the sample.
   */
  averageAdvisorLifecycleMinutes: number | null;
  currentPendingAdvisorAcceptCount: number;
  currentWithAdvisorCount: number;
  sampleSize: {
    acceptLagPairs: number;
    fohCustodyPairs: number;
    /** Slice 5A.3 — complete handoff cycles for this advisor. */
    advisorLifecycleCycles: number;
  };
}

/**
 * Minimum sample size for percentiles. Means are reported for any
 * sample ≥ 1; percentiles need at least 3 points to be informative.
 */
export const PERCENTILE_MIN_SAMPLE = 3;

function parseMs(iso: string): number | null {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Arithmetic mean, or `null` for an empty sample. Exported (Batch 3 —
 * FOH Timer Handoff Analytics) so the shop-level advisor analytics
 * helper reuses the exact same statistic the Passport card uses,
 * keeping the two surfaces from drifting.
 */
export function mean(xs: ReadonlyArray<number>): number | null {
  if (xs.length === 0) return null;
  return xs.reduce((sum, x) => sum + x, 0) / xs.length;
}

/**
 * Nearest-rank percentile. For p = 50 on length 4 sample [a,b,c,d]
 * sorted ascending: rank = ceil(0.5 * 4) = 2 → returns the 2nd
 * element (b). Simple, defensible, and matches the rule the human
 * eye expects when summarising small samples (≥ 3 only).
 *
 * Exported (Batch 3) for reuse by `shopAdvisorAnalytics`.
 */
export function percentile(
  xs: ReadonlyArray<number>,
  p: number,
): number | null {
  if (xs.length < PERCENTILE_MIN_SAMPLE) return null;
  const sorted = [...xs].sort((a, b) => a - b);
  const rank = Math.ceil((p / 100) * sorted.length);
  // Clamp into [1, length] then 0-index.
  const idx = Math.max(1, Math.min(sorted.length, rank)) - 1;
  return sorted[idx];
}

/**
 * Pair sequential custody events per ticket. Walks each ticket's
 * events in ascending `created_at` order; for each `to` event where
 * `actor_id === advisorUserId`, returns the (created_at, lastFrom)
 * pair if the most recent prior event was the matching `from` kind.
 *
 * Returns the elapsed minutes as positive numbers; out-of-order
 * pairs (negative elapsed, clock skew) and unparseable timestamps
 * are dropped.
 */
function pairedDurationsMinutes(
  events: ReadonlyArray<CustodyEventLite>,
  fromKind: CustodyEventKind,
  toKind: CustodyEventKind,
  advisorUserId: string,
): number[] {
  // Group by ticket id; within each, sort ascending by created_at.
  const byTicket = new Map<string, CustodyEventLite[]>();
  for (const ev of events) {
    if (!byTicket.has(ev.ticket_id)) byTicket.set(ev.ticket_id, []);
    byTicket.get(ev.ticket_id)!.push(ev);
  }
  const durations: number[] = [];
  for (const list of byTicket.values()) {
    const sorted = [...list].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    );
    let lastFromMs: number | null = null;
    for (const ev of sorted) {
      if (ev.event_kind === fromKind) {
        lastFromMs = parseMs(ev.created_at);
        continue;
      }
      if (ev.event_kind === toKind && ev.actor_id === advisorUserId) {
        const toMs = parseMs(ev.created_at);
        if (toMs == null || lastFromMs == null) {
          // Reset the from-anchor so we don't carry it past an unparseable.
          lastFromMs = null;
          continue;
        }
        const elapsed = (toMs - lastFromMs) / 60_000;
        if (elapsed >= 0) durations.push(elapsed);
        lastFromMs = null;
      }
    }
  }
  return durations;
}

/**
 * Slice 5A.3 — walks each ticket's events in ascending `created_at`
 * order, anchored on this advisor's `foh_accept`. Returns the minutes
 * elapsed from the immediately-preceding `kick_to_foh` to the next
 * `boh_accept` (any actor — `boh_accept` is by the tech, not the
 * advisor) as a single "advisor lifecycle" cycle duration.
 *
 *   anchor:   `kick_to_foh`  (cycle start)
 *   gate:     `foh_accept`   (must be by this advisor)
 *   terminus: `boh_accept`   (cycle end; closes the cycle)
 *
 * Defensive rules:
 *   - A fresh `kick_to_foh` before `boh_accept` resets the anchor (a
 *     new cycle started without the prior closing).
 *   - A `foh_accept` by a different advisor breaks attribution for the
 *     current cycle (the cycle now belongs to another advisor).
 *   - Negative elapsed (clock skew) is dropped.
 *   - Unparseable timestamps reset state defensively.
 *
 * Note: `kick_to_boh` between `foh_accept` and `boh_accept` is normal
 * (the advisor handing back). The cycle remains attributed to the
 * accepting advisor; we keep walking forward to the `boh_accept`.
 */
function advisorLifecycleCycleMinutes(
  events: ReadonlyArray<CustodyEventLite>,
  advisorUserId: string,
): number[] {
  const byTicket = new Map<string, CustodyEventLite[]>();
  for (const ev of events) {
    if (!byTicket.has(ev.ticket_id)) byTicket.set(ev.ticket_id, []);
    byTicket.get(ev.ticket_id)!.push(ev);
  }
  const durations: number[] = [];
  for (const list of byTicket.values()) {
    const sorted = [...list].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    );
    let cycleStartMs: number | null = null;
    let attributed = false;
    for (const ev of sorted) {
      if (ev.event_kind === 'kick_to_foh') {
        const ms = parseMs(ev.created_at);
        cycleStartMs = ms;
        attributed = false;
        continue;
      }
      if (ev.event_kind === 'foh_accept') {
        if (cycleStartMs == null) {
          attributed = false;
          continue;
        }
        attributed = ev.actor_id === advisorUserId;
        continue;
      }
      if (ev.event_kind === 'boh_accept') {
        if (cycleStartMs != null && attributed) {
          const toMs = parseMs(ev.created_at);
          if (toMs != null) {
            const elapsed = (toMs - cycleStartMs) / 60_000;
            if (elapsed >= 0) durations.push(elapsed);
          }
        }
        cycleStartMs = null;
        attributed = false;
      }
    }
  }
  return durations;
}

export function computeAdvisorMetrics(
  input: AdvisorMetricsInput,
): AdvisorMetrics {
  // ─── Counts from advisor_action_events ───────────────────────────────
  let ticketsAcceptedCount = 0;
  let ticketsKickedBackToTechCount = 0;
  for (const ev of input.advisorActionEvents) {
    if (ev.action_kind === 'ticket_accepted') ticketsAcceptedCount += 1;
    else if (ev.action_kind === 'kicked_back_to_tech') {
      ticketsKickedBackToTechCount += 1;
    }
  }

  // ─── Timing from ticket_custody_events ──────────────────────────────
  const acceptLagsMinutes = pairedDurationsMinutes(
    input.custodyEvents,
    'kick_to_foh',
    'foh_accept',
    input.advisorUserId,
  );
  const fohCustodyMinutes = pairedDurationsMinutes(
    input.custodyEvents,
    'foh_accept',
    'kick_to_boh',
    input.advisorUserId,
  );
  const advisorLifecycleMinutes = advisorLifecycleCycleMinutes(
    input.custodyEvents,
    input.advisorUserId,
  );

  return {
    ticketsAcceptedCount,
    ticketsKickedBackToTechCount,
    averageAcceptLagMinutes: mean(acceptLagsMinutes),
    p50AcceptLagMinutes: percentile(acceptLagsMinutes, 50),
    p95AcceptLagMinutes: percentile(acceptLagsMinutes, 95),
    averageFohCustodyMinutes: mean(fohCustodyMinutes),
    averageAdvisorLifecycleMinutes: mean(advisorLifecycleMinutes),
    currentPendingAdvisorAcceptCount: input.currentCounts.pendingAdvisorAccept,
    currentWithAdvisorCount: input.currentCounts.withAdvisor,
    sampleSize: {
      acceptLagPairs: acceptLagsMinutes.length,
      fohCustodyPairs: fohCustodyMinutes.length,
      advisorLifecycleCycles: advisorLifecycleMinutes.length,
    },
  };
}

/** Default window for the slice 4E hook. Hardcoded; future sub-slice
 *  may expose this to the UI / shop settings. */
export const ADVISOR_METRICS_WINDOW_DAYS = 30;
